import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { Permiso } from '@exp/contracts';
import { Cargando, NoEncontrado, SinPermiso } from './diseno/comunes';
import { Cascara } from './diseno/Cascara';
import { useSesion } from './sesion/SesionProvider';

/**
 * Cada pantalla se pide cuando hace falta, no al abrir la aplicacion.
 *
 * Un consultorio trabaja sobre conexiones que no siempre son buenas, y el
 * expediente se abre muchas veces al dia: lo que se descarga de mas en el
 * primer paso lo paga el medico en cada visita. Las pantallas pesadas —el
 * formulario de colposcopia con su terminologia completa, la historia clinica
 * con sus diez bloques— no tienen por que viajar con la pantalla de acceso.
 *
 * Los componentes se exportan con nombre, no por defecto, asi que hay que
 * adaptar la promesa a lo que espera `lazy`.
 */
const Hoy = lazy(() => import('./rutas/Hoy').then((m) => ({ default: m.Hoy })));
const Acceso = lazy(() => import('./rutas/Acceso').then((m) => ({ default: m.Acceso })));
const SolicitarRestablecimiento = lazy(() =>
  import('./rutas/Restablecer').then((m) => ({ default: m.SolicitarRestablecimiento })),
);
const NuevaContrasena = lazy(() =>
  import('./rutas/Restablecer').then((m) => ({ default: m.NuevaContrasena })),
);
const Registro = lazy(() => import('./rutas/Registro').then((m) => ({ default: m.Registro })));
const Pacientes = lazy(() => import('./rutas/Pacientes').then((m) => ({ default: m.Pacientes })));
const PacienteFormulario = lazy(() =>
  import('./rutas/PacienteFormulario').then((m) => ({ default: m.PacienteFormulario })),
);
const Expediente = lazy(() =>
  import('./rutas/Expediente').then((m) => ({ default: m.Expediente })),
);
const Resumen = lazy(() => import('./rutas/Resumen').then((m) => ({ default: m.Resumen })));
const HistoriaClinica = lazy(() =>
  import('./rutas/HistoriaClinica').then((m) => ({ default: m.HistoriaClinica })),
);
const Atencion = lazy(() => import('./rutas/Atencion').then((m) => ({ default: m.Atencion })));
const Cardiologia = lazy(() =>
  import('./rutas/Cardiologia').then((m) => ({ default: m.Cardiologia })),
);
const Oftalmologia = lazy(() =>
  import('./rutas/Oftalmologia').then((m) => ({ default: m.Oftalmologia })),
);
const Otorrinolaringologia = lazy(() =>
  import('./rutas/Otorrinolaringologia').then((m) => ({ default: m.Otorrinolaringologia })),
);
const Gastroenterologia = lazy(() =>
  import('./rutas/Gastroenterologia').then((m) => ({ default: m.Gastroenterologia })),
);
const PortalPaciente = lazy(() =>
  import('./portal/PortalPaciente').then((m) => ({ default: m.PortalPaciente })),
);
const Planes = lazy(() => import('./rutas/Planes').then((m) => ({ default: m.Planes })));
const Seguimiento = lazy(() =>
  import('./rutas/Seguimiento').then((m) => ({ default: m.Seguimiento })),
);
const Mensajes = lazy(() => import('./rutas/Mensajes').then((m) => ({ default: m.Mensajes })));
const ConversacionDelEquipo = lazy(() =>
  import('./rutas/Mensajes').then((m) => ({ default: m.ConversacionDelEquipo })),
);
const Estudios = lazy(() => import('./rutas/Estudios').then((m) => ({ default: m.Estudios })));
const ColposcopiaNueva = lazy(() =>
  import('./rutas/ColposcopiaNueva').then((m) => ({ default: m.ColposcopiaNueva })),
);
const ColposcopiaDetalle = lazy(() =>
  import('./rutas/ColposcopiaDetalle').then((m) => ({ default: m.ColposcopiaDetalle })),
);
const Consultorio = lazy(() =>
  import('./rutas/Consultorio').then((m) => ({ default: m.Consultorio })),
);
const Usuarios = lazy(() => import('./rutas/Usuarios').then((m) => ({ default: m.Usuarios })));
const Cuenta = lazy(() => import('./rutas/Cuenta').then((m) => ({ default: m.Cuenta })));
const Invitacion = lazy(() =>
  import('./rutas/Invitacion').then((m) => ({ default: m.Invitacion })),
);
const ConsolaPlataforma = lazy(() =>
  import('./plataforma/Consola').then((m) => ({ default: m.ConsolaPlataforma })),
);
const Documentos = lazy(() =>
  import('./rutas/Documentos').then((m) => ({ default: m.Documentos })),
);
const Plantillas = lazy(() =>
  import('./rutas/Plantillas').then((m) => ({ default: m.Plantillas })),
);
const EquipoYAcceso = lazy(() =>
  import('./rutas/EquipoYAcceso').then((m) => ({ default: m.EquipoYAcceso })),
);
const Bitacora = lazy(() => import('./rutas/Bitacora').then((m) => ({ default: m.Bitacora })));
const Agenda = lazy(() => import('./rutas/Agenda').then((m) => ({ default: m.Agenda })));
const AgendaHorarios = lazy(() =>
  import('./rutas/AgendaHorarios').then((m) => ({ default: m.AgendaHorarios })),
);
const PortalAgenda = lazy(() =>
  import('./rutas/PortalAgenda').then((m) => ({ default: m.PortalAgenda })),
);

function RutaProtegida({ children }: { children: ReactNode }) {
  const { usuario, comprobando, sesionVencida } = useSesion();
  const ubicacion = useLocation();

  // Mientras se rehidrata la sesion no se decide nada: mandar al acceso aqui
  // expulsaria a quien solo recargo la pagina.
  if (comprobando) return <Cargando etiqueta="Comprobando su sesión" />;
  if (!usuario) {
    // Se recuerda a donde iba, para devolverla ahi al entrar.
    return (
      <Navigate
        to="/acceso"
        replace
        state={{ desde: ubicacion.pathname + ubicacion.search, vencida: sesionVencida }}
      />
    );
  }

  // Quien recibio una cuenta creada por otra persona cambia la contrasena
  // antes de trabajar. El backend ponia esta bandera desde F1 y nadie la leia:
  // una cuenta creada por el propietario se quedaba con la clave provisional
  // indefinidamente, y esa clave la conocia quien la creo.
  if (usuario.debeCambiarContrasena && ubicacion.pathname !== '/cuenta') {
    return <Navigate to="/cuenta" replace />;
  }

  return <>{children}</>;
}

/**
 * La pantalla entera exige un permiso. Sin el, se dice en lugar de dejar que
 * cada consulta falle con 403. No es seguridad —la del servidor lo es—: es no
 * ensenar una pantalla que no va a funcionar.
 */
function ConPermiso({ permiso, children }: { permiso: Permiso; children: ReactNode }) {
  const { puede } = useSesion();
  return puede(permiso) ? <>{children}</> : <SinPermiso />;
}

/**
 * Arbol de rutas.
 *
 * Plano y sin duplicados por rol. El sistema del que viene este producto tenia
 * un arbol espejo completo que replicaba cada pantalla en modo lectura para un
 * rol; aqui el permiso decide que se puede hacer dentro de una pantalla, no
 * cuantas copias de la pantalla existen.
 */
export function App() {
  return (
    // Frontera para las pantallas que se ven SIN la cascara: acceso y registro.
    // Las de dentro tienen la suya en `Cascara`, para que el riel no parpadee
    // al cambiar de pantalla.
    <Suspense fallback={<Cargando />}>
      <Routes>
        <Route path="/acceso" element={<Acceso />} />
        <Route path="/acceso/:slug" element={<Acceso />} />
        <Route path="/restablecer" element={<SolicitarRestablecimiento />} />
        <Route path="/restablecer/:token" element={<NuevaContrasena />} />
        <Route path="/registro" element={<Registro />} />
        <Route path="/invitacion/:token" element={<Invitacion />} />

        {/* Consola de plataforma (ADR-001): sesion propia, fuera de la cascara y
            de la proteccion de la sesion clinica. No comparte token ni pantallas con
            la clinica. */}
        <Route path="/plataforma/*" element={<ConsolaPlataforma />} />
        {/* Atajo del dueño: el panel de médicos de la consola, con su mismo ingreso. */}
        <Route path="/admin" element={<Navigate to="/plataforma/usuarios" replace />} />

        {/* Portal publico del consultorio. Va FUERA de `RutaProtegida`: quien
            entra aqui no tiene cuenta ni la necesita. Su marca sale del slug,
            no de una sesion. */}
        <Route path="/agenda/:slug" element={<PortalAgenda />} />

        {/* Portal de la paciente (F4.3, ADR-007): sesion propia por cookie, fuera
            de la sesion clinica. No es el portal publico de citas. */}
        <Route path="/portal/*" element={<PortalPaciente />} />

        <Route
          element={
            <RutaProtegida>
              <Cascara />
            </RutaProtegida>
          }
        >
          <Route path="/" element={<Hoy />} />

          <Route
            path="/pacientes"
            element={
              <ConPermiso permiso="pacientes:leer">
                <Pacientes />
              </ConPermiso>
            }
          />
          <Route
            path="/pacientes/nuevo"
            element={
              <ConPermiso permiso="pacientes:crear">
                <PacienteFormulario />
              </ConPermiso>
            }
          />

          <Route
            path="/pacientes/:pacienteId/editar"
            element={
              <ConPermiso permiso="pacientes:editar">
                <PacienteFormulario />
              </ConPermiso>
            }
          />

          <Route path="/pacientes/:pacienteId" element={<Expediente />}>
            <Route index element={<Resumen />} />
            <Route
              path="historia"
              element={
                <ConPermiso permiso="historia:leer">
                  <HistoriaClinica />
                </ConPermiso>
              }
            />
            {/* Cada panel de la atencion decide con su propio permiso. */}
            <Route path="atencion" element={<Atencion />} />
            <Route path="cardiologia" element={<Cardiologia />} />
            <Route path="oftalmologia" element={<Oftalmologia />} />
            <Route path="otorrinolaringologia" element={<Otorrinolaringologia />} />
            <Route path="gastroenterologia" element={<Gastroenterologia />} />
            <Route path="planes" element={<Planes />} />
            <Route
              path="estudios"
              element={
                <ConPermiso permiso="colposcopias:leer">
                  <Estudios />
                </ConPermiso>
              }
            />
            <Route
              path="documentos"
              element={
                <ConPermiso permiso="notas:leer">
                  <Documentos />
                </ConPermiso>
              }
            />
          </Route>

          {/* El formulario y el detalle de un estudio ocupan el ancho completo:
              son documentos, no una pestana del expediente. La identidad de la
              paciente ya no se pierde: la pone la cinta del armazon. */}
          <Route
            path="/pacientes/:pacienteId/estudios/nueva"
            element={
              <ConPermiso permiso="colposcopias:crear">
                <ColposcopiaNueva />
              </ConPermiso>
            }
          />
          {/* Corregir un estudio firmado: la misma captura, precargada, con motivo
              obligatorio. El servidor conserva la version anterior y vuelve a firmar. */}
          <Route
            path="/pacientes/:pacienteId/estudios/:estudioId/corregir"
            element={
              <ConPermiso permiso="colposcopias:editar">
                <ColposcopiaNueva />
              </ConPermiso>
            }
          />
          <Route
            path="/pacientes/:pacienteId/estudios/:estudioId"
            element={
              <ConPermiso permiso="colposcopias:leer">
                <ColposcopiaDetalle />
              </ConPermiso>
            }
          />

          <Route
            path="/agenda"
            element={
              <ConPermiso permiso="citas:leer">
                <Agenda />
              </ConPermiso>
            }
          />

          <Route
            path="/estudios"
            element={
              <ConPermiso permiso="colposcopias:leer">
                <Estudios deTodoElConsultorio />
              </ConPermiso>
            }
          />

          <Route
            path="/seguimiento"
            element={
              <ConPermiso permiso="seguimiento:leer">
                <Seguimiento />
              </ConPermiso>
            }
          />

          {/* La pantalla decide clinico o administrativo con el permiso de cada tipo. */}
          <Route path="/mensajes" element={<Mensajes />} />
          <Route path="/mensajes/:id" element={<ConversacionDelEquipo />} />

          <Route path="/cuenta" element={<Cuenta />} />
          <Route
            path="/consultorio"
            element={
              <ConPermiso permiso="organizacion:leer">
                <Consultorio />
              </ConPermiso>
            }
          />
          <Route
            path="/consultorio/usuarios"
            element={
              <ConPermiso permiso="usuarios:leer">
                <Usuarios />
              </ConPermiso>
            }
          />
          <Route
            path="/consultorio/equipo"
            element={
              <ConPermiso permiso="organizacion:leer">
                <EquipoYAcceso />
              </ConPermiso>
            }
          />
          {/* Los horarios son ajustes del consultorio, no un destino propio. Y
              asi no chocan con `/agenda/:slug`, que es el portal publico. */}
          <Route
            path="/consultorio/agenda"
            element={
              <ConPermiso permiso="citas:leer">
                <AgendaHorarios />
              </ConPermiso>
            }
          />
          <Route
            path="/consultorio/plantillas"
            element={
              <ConPermiso permiso="plantillas:leer">
                <Plantillas />
              </ConPermiso>
            }
          />
          <Route
            path="/consultorio/bitacora"
            element={
              <ConPermiso permiso="auditoria:leer">
                <Bitacora />
              </ConPermiso>
            }
          />

          {/* Una direccion que no existe se dice, no se redirige en silencio:
              quien pego un enlace roto tiene que saber que estaba roto. Va
              dentro del armazon: sin sesion, primero se pide entrar. */}
          <Route
            path="*"
            element={
              <NoEncontrado
                titulo="Esta página no existe"
                ayuda="Revise la dirección o vuelva al inicio."
              />
            }
          />
        </Route>
      </Routes>
    </Suspense>
  );
}
